import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import GenderSelect from "./GenderSelect";
import { ChevronDown, ChevronUp, Plus, Trash2, Users, Phone, Shield, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { BirthdayDatePicker, formatDateForStorage, parseDateFromStorage } from "@/components/ui/birthday-date-picker";
import { formatPhoneNumber } from "@/lib/format";

// List of US states for dropdown
const usStates = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  // US Territories
  { value: 'AS', label: 'American Samoa' },
  { value: 'GU', label: 'Guam' },
  { value: 'MP', label: 'Northern Mariana Islands' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'VI', label: 'U.S. Virgin Islands' }
];

interface EmergencyContact {
  id?: string;
  name: string;
  relationship: string;
  phone: string;
  is_primary: boolean;
}

interface PickupAuthorization {
  id?: string;
  name: string;
  relationship: string;
  phone: string;
  photo_id_required: boolean;
}

interface StudentFormData {
  first_name: string | undefined;
  last_name: string | undefined;
  preferred_name: string | undefined;
  date_of_birth: string | undefined;
  gender: string | undefined;
  notes: string | undefined;
  
  // Contact information
  address_line1: string | undefined;
  address_line2: string | undefined;
  city: string | undefined;
  state: string | undefined;
  zip: string | undefined;
  phone: string | undefined;
  email: string | undefined;
  
  // Parent address link
  uses_parent_address: boolean | undefined;
  parent_id_for_address: string | undefined;
  
  // Emergency contacts
  emergency_contacts: EmergencyContact[] | undefined;
  
  // Pickup authorizations
  pickup_authorizations: PickupAuthorization[] | undefined;
  
  // Additional info
  location_id: string | undefined;
  profile_picture_url: string | undefined;
  start_date: string | undefined;
  drop_date: string | undefined;
  is_active: boolean | undefined;
  no_public_photos: boolean | undefined;
}

interface StudentFormFieldsProps {
  formData: StudentFormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleGenderChange: (value: string) => void;
  handleSwitchChange?: (name: string, checked: boolean) => void;
  handleStateChange?: (value: string) => void;
  locations?: { id: string; name: string }[];
  handleImageUpload?: (file: File) => Promise<void>;
  familyParents?: { id: string; first_name: string; last_name: string; address_street?: string; address_city?: string; address_state?: string; address_zip?: string; }[];
  handleParentAddressChange?: (parentId: string | null) => void;
  handleEmergencyContactChange?: (contacts: EmergencyContact[]) => void;
  handlePickupAuthorizationChange?: (authorizations: PickupAuthorization[]) => void;
}

const StudentFormFields: React.FC<StudentFormFieldsProps> = ({
  formData,
  handleChange,
  handleGenderChange,
  handleSwitchChange,
  handleStateChange,
  locations,
  handleImageUpload,
  familyParents,
  handleParentAddressChange,
  handleEmergencyContactChange,
  handlePickupAuthorizationChange,
}) => {
  // Remove collapsible state - show all sections by default
  const [previewUrl, setPreviewUrl] = useState<string | null>(formData.profile_picture_url || null);
  const [formattedPhone, setFormattedPhone] = useState(formData.phone || '');
  const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContact[]>(
    formData.emergency_contacts || [{name: '', relationship: '', phone: '', is_primary: true}]
  );
  const [pickupAuthorizations, setPickupAuthorizations] = useState<PickupAuthorization[]>(
    formData.pickup_authorizations || [{name: '', relationship: '', phone: '', photo_id_required: true}]
  );

  // Format phone number when the component mounts or formData.phone changes
  useEffect(() => {
    if (formData.phone) {
      setFormattedPhone(formatPhoneNumber(formData.phone));
    }
  }, [formData.phone]);

  // Initialize emergency contacts or update from props
  useEffect(() => {
    if (formData.emergency_contacts && formData.emergency_contacts.length > 0) {
      setEmergencyContacts(formData.emergency_contacts);
    }
  }, [formData.emergency_contacts]);

  // Initialize pickup authorizations or update from props
  useEffect(() => {
    if (formData.pickup_authorizations && formData.pickup_authorizations.length > 0) {
      setPickupAuthorizations(formData.pickup_authorizations);
    }
  }, [formData.pickup_authorizations]);

  const onImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Set preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
    
    // Upload file if handler provided
    if (handleImageUpload) {
      await handleImageUpload(file);
    }
  };

  const onSwitchChange = (name: string, checked: boolean) => {
    if (handleSwitchChange) {
      handleSwitchChange(name, checked);
    }
  };

  // Toggle parent address
  const toggleUseParentAddress = (checked: boolean) => {
    // Clear the parent ID if unchecking
    if (!checked && handleParentAddressChange) {
      handleParentAddressChange(null);
      // Clear address fields when toggling off
      const addressFields = [
        { name: 'address_line1', value: '' },
        { name: 'city', value: '' },
        { name: 'state', value: '' },
        { name: 'zip', value: '' }
      ];

      addressFields.forEach(field => {
        const syntheticEvent = {
          target: {
            name: field.name,
            value: field.value
          }
        } as React.ChangeEvent<HTMLInputElement>;
        
        handleChange(syntheticEvent);
      });
    } else if (checked && familyParents && familyParents.length === 1) {
      // If only one parent, automatically select and copy their address
      const parent = familyParents[0];
      onParentSelect(parent.id);
    }
    
    if (handleSwitchChange) {
      handleSwitchChange('uses_parent_address', checked);
    }
  };

  // Select parent for address and copy their address
  const onParentSelect = (parentId: string) => {
    if (handleParentAddressChange) {
      handleParentAddressChange(parentId);
    } else {
      // Create a synthetic event for parent_id_for_address
      const syntheticEvent = {
        target: {
          name: 'parent_id_for_address',
          value: parentId
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      handleChange(syntheticEvent);
    }

    // Copy parent's address to student fields
    if (familyParents && parentId) {
      const selectedParent = familyParents.find(p => p.id === parentId);
      if (selectedParent) {
        // Create synthetic events to update student address fields
        const addressFields = [
          { name: 'address_line1', value: selectedParent.address_street || '' },
          { name: 'city', value: selectedParent.address_city || '' },
          { name: 'state', value: selectedParent.address_state || '' },
          { name: 'zip', value: selectedParent.address_zip || '' }
        ];

        addressFields.forEach(field => {
          const syntheticEvent = {
            target: {
              name: field.name,
              value: field.value
            }
          } as React.ChangeEvent<HTMLInputElement>;
          
          handleChange(syntheticEvent);
        });
      }
    }
  };

  // Handle phone input with formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setFormattedPhone(formatted);
    
    // Create a synthetic event to pass to handleChange
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        name: 'phone',
        value: formatted
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    handleChange(syntheticEvent);
  };

  const onStateChange = (value: string) => {
    if (handleStateChange) {
      handleStateChange(value);
    } else {
      // Create a synthetic event to pass to handleChange
      const syntheticEvent = {
        target: {
          name: 'state',
          value: value
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      handleChange(syntheticEvent);
    }
  };

  // Emergency contact handlers
  const addEmergencyContact = () => {
    const newContacts = [...emergencyContacts, {
      name: '',
      relationship: '',
      phone: '',
      is_primary: false
    }];
    
    setEmergencyContacts(newContacts);
    
    if (handleEmergencyContactChange) {
      handleEmergencyContactChange(newContacts);
    }
  };

  const removeEmergencyContact = (index: number) => {
    if (emergencyContacts.length <= 1) return;
    
    const newContacts = emergencyContacts.filter((_, i) => i !== index);
    
    // If we removed the primary contact and there are other contacts left,
    // make the first one primary
    if (emergencyContacts[index].is_primary && newContacts.length > 0) {
      newContacts[0].is_primary = true;
    }
    
    setEmergencyContacts(newContacts);
    
    if (handleEmergencyContactChange) {
      handleEmergencyContactChange(newContacts);
    }
  };

  const updateEmergencyContact = (index: number, field: keyof EmergencyContact, value: any) => {
    const updatedContacts = [...emergencyContacts];
    updatedContacts[index] = { ...updatedContacts[index], [field]: value };
    
    // If setting as primary, update other contacts to not be primary
    if (field === 'is_primary' && value === true) {
      updatedContacts.forEach((contact, i) => {
        if (i !== index) {
          contact.is_primary = false;
        }
      });
    }
    
    setEmergencyContacts(updatedContacts);
    
    if (handleEmergencyContactChange) {
      handleEmergencyContactChange(updatedContacts);
    }
  };

  // Format emergency contact phone
  const formatEmergencyContactPhone = (index: number, phone: string) => {
    const formatted = formatPhoneNumber(phone);
    updateEmergencyContact(index, 'phone', formatted);
  };

  // Pickup authorization handlers
  const addPickupAuthorization = () => {
    const newAuthorizations = [...pickupAuthorizations, {
      name: '',
      relationship: '',
      phone: '',
      photo_id_required: true
    }];
    
    setPickupAuthorizations(newAuthorizations);
    
    if (handlePickupAuthorizationChange) {
      handlePickupAuthorizationChange(newAuthorizations);
    }
  };

  const removePickupAuthorization = (index: number) => {
    if (pickupAuthorizations.length <= 1) return;
    
    const newAuthorizations = pickupAuthorizations.filter((_, i) => i !== index);
    setPickupAuthorizations(newAuthorizations);
    
    if (handlePickupAuthorizationChange) {
      handlePickupAuthorizationChange(newAuthorizations);
    }
  };

  const updatePickupAuthorization = (index: number, field: keyof PickupAuthorization, value: any) => {
    const updatedAuthorizations = [...pickupAuthorizations];
    updatedAuthorizations[index] = { ...updatedAuthorizations[index], [field]: value };
    
    setPickupAuthorizations(updatedAuthorizations);
    
    if (handlePickupAuthorizationChange) {
      handlePickupAuthorizationChange(updatedAuthorizations);
    }
  };

  // Format pickup authorization phone
  const formatPickupAuthorizationPhone = (index: number, phone: string) => {
    const formatted = formatPhoneNumber(phone);
    updatePickupAuthorization(index, 'phone', formatted);
  };

  return (
    <div className="space-y-8">
      {/* Personal Information */}
      <Card className="border border-blue-200 bg-blue-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Users className="h-4 w-4 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-blue-900">Personal Information</h3>
          </div>
          
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name || ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name || ""}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="preferred_name">Preferred Name</Label>
              <Input
                id="preferred_name"
                name="preferred_name"
                value={formData.preferred_name || ""}
                onChange={handleChange}
                placeholder="Nickname or preferred name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="date_of_birth">Date of Birth</Label>
              <BirthdayDatePicker
                value={formData.date_of_birth ? parseDateFromStorage(formData.date_of_birth) : undefined}
                onChange={(date) => {
                  const syntheticEvent = {
                    target: {
                      name: "date_of_birth",
                      value: date ? formatDateForStorage(date) : ""
                    }
                  } as React.ChangeEvent<HTMLInputElement>;
                  handleChange(syntheticEvent);
                }}
                placeholder="Select date of birth"
              />
            </div>
            
            <GenderSelect 
              value={formData.gender || ""} 
              onChange={handleGenderChange} 
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Contact Information */}
      <Card className="border border-purple-200 bg-purple-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <Phone className="h-4 w-4 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-purple-900">Contact Information</h3>
          </div>
          
          <div className="grid gap-4">
            {/* Same as Parent Address Toggle */}
            <div className={`flex items-center justify-between space-x-2 border p-3 rounded-md ${
              !familyParents || familyParents.length === 0 
                ? 'border-orange-200 bg-orange-50' 
                : 'border-gray-200'
            }`}>
              <div>
                <Label htmlFor="uses_parent_address" className="text-base">Same as Parent Address</Label>
                <p className="text-sm text-muted-foreground">
                  {familyParents && familyParents.length > 0 
                    ? "Copy address from selected parent" 
                    : "⚠️ Student not assigned to a family - please assign to family first"}
                </p>
              </div>
              <Switch
                id="uses_parent_address"
                checked={formData.uses_parent_address === true}
                onCheckedChange={toggleUseParentAddress}
                disabled={!familyParents || familyParents.length === 0}
              />
            </div>
            
            {!familyParents || familyParents.length === 0 && (
              <div className="text-xs text-orange-600 bg-orange-100 p-2 rounded-md">
                💡 To use a parent's address, first assign this student to a family that has parents with address information.
              </div>
            )}
                
                {/* Parent Address Selection */}
                {formData.uses_parent_address && familyParents && familyParents.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="parent_id_for_address">Select Parent</Label>
                    <Select
                      value={formData.parent_id_for_address || ""}
                      onValueChange={onParentSelect}
                    >
                      <SelectTrigger id="parent_id_for_address">
                        <SelectValue placeholder="Select parent" />
                      </SelectTrigger>
                      <SelectContent>
                        {familyParents.map(parent => (
                          <SelectItem key={parent.id} value={parent.id}>
                            {`${parent.first_name} ${parent.last_name}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Display selected parent's address */}
                    {formData.parent_id_for_address && (
                      <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded-md text-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-4 h-4 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-xs text-purple-600">✓</span>
                          </div>
                          <span className="text-sm font-medium text-purple-800">Address copied from parent:</span>
                        </div>
                        <div className="text-purple-700">
                          {(() => {
                            const selectedParent = familyParents.find(p => p.id === formData.parent_id_for_address);
                            if (!selectedParent) return "No address information";
                            
                            const addressParts = [
                              selectedParent.address_street,
                              selectedParent.address_city,
                              selectedParent.address_state,
                              selectedParent.address_zip
                            ].filter(Boolean);
                            
                            return addressParts.length > 0 
                              ? addressParts.join(", ") 
                              : "No address information available for this parent";
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Regular Address Fields - only show if not using parent address */}
                {!formData.uses_parent_address && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="address_line1">Address Line 1</Label>
                      <Input
                        id="address_line1"
                        name="address_line1"
                        value={formData.address_line1 || ""}
                        onChange={handleChange}
                        placeholder="123 Main St"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="address_line2">Address Line 2</Label>
                      <Input
                        id="address_line2"
                        name="address_line2"
                        value={formData.address_line2 || ""}
                        onChange={handleChange}
                        placeholder="Apt, Suite, etc."
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city || ""}
                          onChange={handleChange}
                          placeholder="City"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="state">State</Label>
                        <Select
                          value={formData.state || ""}
                          onValueChange={(value) => onStateChange(value)}
                        >
                          <SelectTrigger id="state">
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {usStates.map(state => (
                              <SelectItem key={state.value} value={state.value}>
                                {state.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="zip">ZIP Code</Label>
                        <Input
                          id="zip"
                          name="zip"
                          value={formData.zip || ""}
                          onChange={handleChange}
                          placeholder="12345"
                          maxLength={10}
                        />
                      </div>
                    </div>
                  </>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formattedPhone}
                    onChange={(e) => handlePhoneChange(e)}
                    placeholder="(555) 123-4567"
                    maxLength={14}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email || ""}
                    onChange={handleChange}
                    placeholder="student@example.com"
                  />
                </div>
              </div>
        </CardContent>
      </Card>
      
      {/* Emergency Contacts */}
      <Card className="border border-destructive/20 bg-destructive/10">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-destructive/20 rounded-full flex items-center justify-center">
              <AlertCircle className="h-4 w-4 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-destructive">Emergency Contacts</h3>
          </div>
          
          <div className="grid gap-4">
            {emergencyContacts.map((contact, index) => (
                  <div key={index} className="border p-4 rounded-md">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-semibold">Emergency Contact {index + 1}</h4>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeEmergencyContact(index)}
                        disabled={emergencyContacts.length <= 1}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                    
                    <div className="grid gap-3">
                      <div className="space-y-2">
                        <Label htmlFor={`ec-name-${index}`}>Name</Label>
                        <Input
                          id={`ec-name-${index}`}
                          value={contact.name}
                          onChange={(e) => updateEmergencyContact(index, 'name', e.target.value)}
                          placeholder="Full Name"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor={`ec-relation-${index}`}>Relationship</Label>
                        <Input
                          id={`ec-relation-${index}`}
                          value={contact.relationship}
                          onChange={(e) => updateEmergencyContact(index, 'relationship', e.target.value)}
                          placeholder="Parent, Grandparent, etc."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor={`ec-phone-${index}`}>Phone</Label>
                        <Input
                          id={`ec-phone-${index}`}
                          value={contact.phone}
                          onChange={(e) => formatEmergencyContactPhone(index, e.target.value)}
                          placeholder="(555) 123-4567"
                          maxLength={14}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between space-x-2 border p-2 rounded-md">
                        <div>
                          <Label htmlFor={`ec-primary-${index}`} className="text-sm">Primary Contact</Label>
                          <p className="text-xs text-muted-foreground">Contact first in emergency</p>
                        </div>
                        <Switch
                          id={`ec-primary-${index}`}
                          checked={contact.is_primary}
                          onCheckedChange={(checked) => updateEmergencyContact(index, 'is_primary', checked)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
            <Button
              type="button"
              variant="outline"
              className="mt-2"
              onClick={addEmergencyContact}
            >
              <Plus size={16} className="mr-2" />
              Add Emergency Contact
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Dropoff & Pickup Authorization - NEW */}
      <Card className="border border-green-200 bg-green-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Shield className="h-4 w-4 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-green-900">Dropoff & Pickup Authorization</h3>
          </div>
          
          <div className="grid gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-green-800 mb-2">Authorized Pickup Persons</h4>
              <p className="text-sm text-green-700 mb-3">
                These individuals are authorized to pick up this student. Photo ID may be required.
              </p>
              
              <div className="space-y-4">
                {pickupAuthorizations.map((authorization, index) => (
                  <div key={index} className="border border-green-300 bg-white rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h5 className="text-sm font-semibold text-green-800">Authorized Person {index + 1}</h5>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removePickupAuthorization(index)}
                        disabled={pickupAuthorizations.length <= 1}
                        className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                    
                    <div className="grid gap-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor={`pickup-name-${index}`}>Full Name</Label>
                          <Input
                            id={`pickup-name-${index}`}
                            value={authorization.name}
                            onChange={(e) => updatePickupAuthorization(index, 'name', e.target.value)}
                            placeholder="Enter full name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`pickup-relationship-${index}`}>Relationship</Label>
                          <Input
                            id={`pickup-relationship-${index}`}
                            value={authorization.relationship}
                            onChange={(e) => updatePickupAuthorization(index, 'relationship', e.target.value)}
                            placeholder="Parent, Grandparent, etc."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`pickup-phone-${index}`}>Phone Number</Label>
                          <Input
                            id={`pickup-phone-${index}`}
                            value={authorization.phone}
                            onChange={(e) => formatPickupAuthorizationPhone(index, e.target.value)}
                            placeholder="(555) 123-4567"
                            maxLength={14}
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between space-x-2 border border-green-200 p-3 rounded-md bg-green-50">
                        <div>
                          <Label htmlFor={`pickup-photo-id-${index}`} className="text-sm">Photo ID Required</Label>
                          <p className="text-xs text-muted-foreground">Require photo identification for pickup</p>
                        </div>
                        <Switch
                          id={`pickup-photo-id-${index}`}
                          checked={authorization.photo_id_required}
                          onCheckedChange={(checked) => updatePickupAuthorization(index, 'photo_id_required', checked)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                <Button
                  type="button"
                  variant="outline"
                  className="mt-2 border-green-300 text-green-700 hover:bg-green-50"
                  onClick={addPickupAuthorization}
                >
                  <Plus size={16} className="mr-2" />
                  Add Authorized Person
                </Button>
                
                <div className="text-xs text-green-600 bg-green-100 p-2 rounded-md">
                  💡 Emergency contacts are automatically authorized for pickup and do not need to be added here separately.
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Additional Information */}
      <Card className="border border-gray-200 bg-gray-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
              <Users className="h-4 w-4 text-gray-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
          </div>
          
          <div className="grid gap-4">
            {/* Profile Picture */}
                <div className="space-y-2">
                  <Label htmlFor="profile_picture">Profile Picture</Label>
                  <div className="flex items-center gap-4">
                    {previewUrl && (
                      <div className="relative w-20 h-20 rounded-full overflow-hidden bg-muted">
                        <img 
                          src={previewUrl} 
                          alt="Profile preview" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <Input
                        id="profile_picture"
                        name="profile_picture"
                        type="file"
                        accept="image/*"
                        onChange={onImageChange}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Location */}
                {locations && locations.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="location_id">Location</Label>
                    <Select
                      value={formData.location_id || ""}
                      onValueChange={(value) => {
                        // Create a synthetic event to pass to handleChange
                        const syntheticEvent = {
                          target: {
                            name: 'location_id',
                            value: value
                          }
                        } as React.ChangeEvent<HTMLInputElement>;
                        
                        handleChange(syntheticEvent);
                      }}
                    >
                      <SelectTrigger id="location_id">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((location) => (
                          <SelectItem key={location.id} value={location.id}>
                            {location.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date</Label>
                    <DatePicker
                      value={formData.start_date ? new Date(formData.start_date) : undefined}
                      onChange={(date) => {
                        const syntheticEvent = {
                          target: {
                            name: "start_date",
                            value: date ? date.toISOString().split('T')[0] : ""
                          }
                        } as React.ChangeEvent<HTMLInputElement>;
                        handleChange(syntheticEvent);
                      }}
                      placeholder="Select start date"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="drop_date">Drop Date</Label>
                    <DatePicker
                      value={formData.drop_date ? new Date(formData.drop_date) : undefined}
                      onChange={(date) => {
                        const syntheticEvent = {
                          target: {
                            name: "drop_date",
                            value: date ? date.toISOString().split('T')[0] : ""
                          }
                        } as React.ChangeEvent<HTMLInputElement>;
                        handleChange(syntheticEvent);
                      }}
                      placeholder="Select drop date"
                      minDate={formData.start_date ? new Date(formData.start_date) : undefined}
                    />
                  </div>
                </div>
                
                {/* Active status */}
                <div className="flex items-center justify-between space-x-2 border p-3 rounded-md">
                  <div>
                    <Label htmlFor="is_active" className="text-base">Active Student</Label>
                    <p className="text-sm text-muted-foreground">Student is currently active</p>
                  </div>
                  <Switch
                    id="is_active"
                    checked={formData.is_active !== false}
                    onCheckedChange={(checked) => onSwitchChange('is_active', checked)}
                  />
                </div>
                
                {/* Photo consent */}
                <div className="flex items-center justify-between space-x-2 border p-3 rounded-md">
                  <div>
                    <Label htmlFor="no_public_photos" className="text-base">No Public Photos</Label>
                    <p className="text-sm text-muted-foreground">Do not post public photos of this student</p>
                  </div>
                  <Switch
                    id="no_public_photos"
                    checked={formData.no_public_photos === true}
                    onCheckedChange={(checked) => onSwitchChange('no_public_photos', checked)}
                  />
              </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Notes */}
      <Card className="border border-yellow-200 bg-yellow-50/30">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
              <Users className="h-4 w-4 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-yellow-900">Notes</h3>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              name="notes"
              value={formData.notes || ""}
              onChange={handleChange}
              rows={3}
              placeholder="Any additional information about this student..."
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentFormFields;
